import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useAuth } from '@/hooks/useAuth';
import { usePosConfig, useUpsertPosConfig } from '@/hooks/api/useRetailPosConfig';
import { UserRole, type RetailPosSettings } from '@pms/shared';

const ADMIN_ROLES = new Set<UserRole>([UserRole.ADMIN, UserRole.SUPER_ADMIN]);

const TABS: { param: string; label: string }[] = [
  { param: 'pos', label: 'POS' },
  { param: 'order', label: 'Order' },
  { param: 'night-audit', label: 'Night Audit' },
  { param: 'shift', label: 'Shift' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Page ────────────────────────────────────────────────────────────────────

export function RetailConfigPage() {
  const propertyId = useSelectedPropertyId();
  const { user } = useAuth();
  const canManage = !!user && ADMIN_ROLES.has(user.role);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeParam = searchParams.get('tab') ?? 'pos';
  const activeTab = TABS.find((t) => t.param === activeParam) ?? TABS[0];

  const settingsQuery = usePosConfig(propertyId || null);

  function switchTab(param: string, dirty: boolean) {
    if (param === activeTab.param) return;
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    setSearchParams({ tab: param });
  }

  if (user && !canManage) {
    return (
      <div>
        <PageHeader title="App Config" description="RetailKey POS settings" />
        <EmptyState icon={Lock} title="No access" description="You don't have access to POS configuration." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="App Config" description="POS, order, night-audit and shift settings" />

      {!propertyId ? (
        <EmptyState icon={Lock} title="Select a property" description="Select a property to continue." />
      ) : settingsQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : settingsQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(settingsQuery.error)}
        </p>
      ) : (
        <ConfigTabs
          propertyId={propertyId}
          settings={settingsQuery.data ?? null}
          activeParam={activeTab.param}
          onSwitchTab={switchTab}
        />
      )}
    </div>
  );
}

// ─── Tabs wrapper (owns dirty-aware tab switching) ───────────────────────────

function ConfigTabs({
  propertyId,
  settings,
  activeParam,
  onSwitchTab,
}: {
  propertyId: string;
  settings: RetailPosSettings | null;
  activeParam: string;
  onSwitchTab: (param: string, dirty: boolean) => void;
}) {
  const [dirty, setDirty] = useState(false);

  // beforeunload guard while a tab has unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const tabProps = {
    propertyId,
    settings,
    onDirtyChange: setDirty,
  };

  return (
    <div>
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Config sections">
          {TABS.map((t) => (
            <button
              key={t.param}
              type="button"
              onClick={() => onSwitchTab(t.param, dirty)}
              className={cn(
                'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium',
                t.param === activeParam
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              )}
              aria-current={t.param === activeParam ? 'page' : undefined}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* key forces a fresh form (+ dirty reset) per tab. */}
      {activeParam === 'pos' && <PosTab key="pos" {...tabProps} />}
      {activeParam === 'order' && <OrderTab key="order" {...tabProps} />}
      {activeParam === 'night-audit' && <NightAuditTab key="night-audit" {...tabProps} />}
      {activeParam === 'shift' && <ShiftTab key="shift" {...tabProps} />}
    </div>
  );
}

// ─── Shared form chrome ──────────────────────────────────────────────────────

interface TabProps {
  propertyId: string;
  settings: RetailPosSettings | null;
  onDirtyChange: (dirty: boolean) => void;
}

function FormShell({
  children,
  onSubmit,
  saveDisabled,
  isPending,
  error,
  success,
}: {
  children: React.ReactNode;
  onSubmit: (e: React.FormEvent) => void;
  saveDisabled: boolean;
  isPending: boolean;
  error: unknown;
  success: boolean;
}) {
  return (
    <form onSubmit={onSubmit} className="bg-white rounded-xl border border-gray-200 p-4 space-y-4 max-w-xl">
      {children}

      {error != null && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(error)}
        </p>
      )}
      {success && (
        <p className="text-sm text-green-600" role="status">Settings saved.</p>
      )}

      <div className="flex justify-end pt-2 border-t border-gray-100">
        <Button type="submit" isLoading={isPending} disabled={saveDisabled}>Save changes</Button>
      </div>
    </form>
  );
}

function Field({ id, label, children, error }: { id: string; label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function Checkbox({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
      <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}</label>
    </div>
  );
}

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

// ─── POS tab ─────────────────────────────────────────────────────────────────

function PosTab({ propertyId, settings, onDirtyChange }: TabProps) {
  const upsert = useUpsertPosConfig();
  const [taxRate, setTaxRate] = useState('');
  const [receiptHeader, setReceiptHeader] = useState('');
  const [receiptFooter, setReceiptFooter] = useState('');
  const [defaultView, setDefaultView] = useState<'grid' | 'list'>('grid');
  const [minAge, setMinAge] = useState('');
  const [bottleDeposit, setBottleDeposit] = useState('');
  const [gatewayEnabled, setGatewayEnabled] = useState(false);

  useEffect(() => {
    setTaxRate(settings?.taxRatePercent != null ? parseFloat(settings.taxRatePercent).toFixed(2) : '');
    setReceiptHeader(settings?.receiptHeader ?? '');
    setReceiptFooter(settings?.receiptFooter ?? '');
    setDefaultView(settings?.defaultTerminalView ?? 'grid');
    setMinAge(settings?.minAge != null ? String(settings.minAge) : '');
    setBottleDeposit(settings?.bottleDeposit != null ? parseFloat(settings.bottleDeposit).toFixed(2) : '');
    setGatewayEnabled(settings?.paymentGatewayEnabled ?? false);
    onDirtyChange(false);
  }, [settings, onDirtyChange]);

  const taxNum = parseFloat(taxRate);
  const taxError = taxRate.trim() !== '' && (Number.isNaN(taxNum) || taxNum < 0 || taxNum > 100)
    ? 'Tax rate must be between 0 and 100.' : '';
  const depositNum = parseFloat(bottleDeposit);
  const depositError = bottleDeposit.trim() !== '' && (Number.isNaN(depositNum) || depositNum < 0)
    ? 'Deposit must be 0 or more.' : '';
  const hasError = !!taxError || !!depositError;

  function touch() { onDirtyChange(true); if (upsert.isError) upsert.reset(); }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (hasError) return;
    upsert.mutate(
      {
        propertyId,
        taxRatePercent: taxRate.trim() === '' ? '0.00' : taxNum.toFixed(2),
        receiptHeader: receiptHeader.trim() || null,
        receiptFooter: receiptFooter.trim() || null,
        defaultTerminalView: defaultView,
        minAge: minAge.trim() === '' ? null : Number(minAge),
        bottleDeposit: bottleDeposit.trim() === '' ? null : depositNum.toFixed(2),
        paymentGatewayEnabled: gatewayEnabled,
      },
      { onSuccess: () => onDirtyChange(false) },
    );
  }

  return (
    <FormShell onSubmit={handleSubmit} saveDisabled={hasError} isPending={upsert.isPending} error={upsert.error} success={upsert.isSuccess}>
      <Field id="cfg-tax" label="Tax rate (%)" error={taxError}>
        <input id="cfg-tax" type="number" step="0.01" min="0" max="100" value={taxRate} onChange={(e) => { setTaxRate(e.target.value); touch(); }} className={inputCls} />
      </Field>
      <Field id="cfg-view" label="Default terminal view">
        <select id="cfg-view" value={defaultView} onChange={(e) => { setDefaultView(e.target.value as 'grid' | 'list'); touch(); }} className={`${inputCls} bg-white`}>
          <option value="grid">Grid</option>
          <option value="list">List</option>
        </select>
      </Field>
      <Field id="cfg-minage" label="Minimum age">
        <input id="cfg-minage" type="number" min="0" value={minAge} onChange={(e) => { setMinAge(e.target.value); touch(); }} placeholder="None" className={inputCls} />
      </Field>
      <Field id="cfg-deposit" label="Bottle deposit" error={depositError}>
        <input id="cfg-deposit" type="number" step="0.01" min="0" value={bottleDeposit} onChange={(e) => { setBottleDeposit(e.target.value); touch(); }} placeholder="0.00" className={inputCls} />
      </Field>
      <Field id="cfg-rh" label="Receipt header">
        <textarea id="cfg-rh" rows={2} value={receiptHeader} onChange={(e) => { setReceiptHeader(e.target.value); touch(); }} className={inputCls} />
      </Field>
      <Field id="cfg-rf" label="Receipt footer">
        <textarea id="cfg-rf" rows={2} value={receiptFooter} onChange={(e) => { setReceiptFooter(e.target.value); touch(); }} className={inputCls} />
      </Field>
      <Checkbox id="cfg-gateway" label="Payment gateway enabled" checked={gatewayEnabled} onChange={(v) => { setGatewayEnabled(v); touch(); }} />
    </FormShell>
  );
}

// ─── Order tab ───────────────────────────────────────────────────────────────

function OrderTab({ propertyId, settings, onDirtyChange }: TabProps) {
  const upsert = useUpsertPosConfig();
  const [prefix, setPrefix] = useState('');
  const [autoPrint, setAutoPrint] = useState(false);
  const [requireName, setRequireName] = useState(false);
  const [allowDiscounts, setAllowDiscounts] = useState(false);
  const [maxDiscount, setMaxDiscount] = useState('');

  useEffect(() => {
    setPrefix(settings?.orderNumberPrefix ?? '');
    setAutoPrint(settings?.autoPrintReceipt ?? false);
    setRequireName(settings?.requireCustomerName ?? false);
    setAllowDiscounts(settings?.allowDiscounts ?? false);
    setMaxDiscount(settings?.maxDiscountPercent != null ? parseFloat(settings.maxDiscountPercent).toFixed(2) : '');
    onDirtyChange(false);
  }, [settings, onDirtyChange]);

  const discNum = parseFloat(maxDiscount);
  const discError = allowDiscounts && maxDiscount.trim() !== '' && (Number.isNaN(discNum) || discNum < 0 || discNum > 100)
    ? 'Max discount must be between 0 and 100.' : '';

  function touch() { onDirtyChange(true); if (upsert.isError) upsert.reset(); }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (discError) return;
    upsert.mutate(
      {
        propertyId,
        orderNumberPrefix: prefix.trim() || null,
        autoPrintReceipt: autoPrint,
        requireCustomerName: requireName,
        allowDiscounts,
        maxDiscountPercent: allowDiscounts && maxDiscount.trim() !== '' ? discNum.toFixed(2) : null,
      },
      { onSuccess: () => onDirtyChange(false) },
    );
  }

  return (
    <FormShell onSubmit={handleSubmit} saveDisabled={!!discError} isPending={upsert.isPending} error={upsert.error} success={upsert.isSuccess}>
      <Field id="cfg-prefix" label="Order number prefix">
        <input id="cfg-prefix" type="text" value={prefix} onChange={(e) => { setPrefix(e.target.value); touch(); }} className={inputCls} />
      </Field>
      <Checkbox id="cfg-autoprint" label="Auto-print receipt" checked={autoPrint} onChange={(v) => { setAutoPrint(v); touch(); }} />
      <Checkbox id="cfg-reqname" label="Require customer name" checked={requireName} onChange={(v) => { setRequireName(v); touch(); }} />
      <Checkbox id="cfg-allowdisc" label="Allow discounts" checked={allowDiscounts} onChange={(v) => { setAllowDiscounts(v); touch(); }} />
      <Field id="cfg-maxdisc" label="Max discount (%)" error={discError}>
        <input id="cfg-maxdisc" type="number" step="0.01" min="0" max="100" disabled={!allowDiscounts} value={maxDiscount} onChange={(e) => { setMaxDiscount(e.target.value); touch(); }} className={cn(inputCls, !allowDiscounts && 'bg-gray-100 text-gray-400')} />
      </Field>
    </FormShell>
  );
}

// ─── Night Audit tab ─────────────────────────────────────────────────────────

function NightAuditTab({ propertyId, settings, onDirtyChange }: TabProps) {
  const upsert = useUpsertPosConfig();
  const [autoRun, setAutoRun] = useState(false);
  const [autoRunTime, setAutoRunTime] = useState('');
  const [warnOpenShifts, setWarnOpenShifts] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [recipientsError, setRecipientsError] = useState('');

  useEffect(() => {
    setAutoRun(settings?.autoRunNightAudit ?? false);
    setAutoRunTime(settings?.autoRunTime ?? '');
    setWarnOpenShifts(settings?.warnOnOpenShifts ?? false);
    setRecipients((settings?.auditRecipients ?? []).join(', '));
    setRecipientsError('');
    onDirtyChange(false);
  }, [settings, onDirtyChange]);

  function parseRecipients(): string[] {
    return recipients.split(',').map((r) => r.trim()).filter(Boolean);
  }

  function validateRecipients() {
    const bad = parseRecipients().find((r) => !EMAIL_RE.test(r));
    setRecipientsError(bad ? `"${bad}" is not a valid email.` : '');
  }

  function touch() { onDirtyChange(true); if (upsert.isError) upsert.reset(); }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const list = parseRecipients();
    const bad = list.find((r) => !EMAIL_RE.test(r));
    if (bad) { setRecipientsError(`"${bad}" is not a valid email.`); return; }
    upsert.mutate(
      {
        propertyId,
        autoRunNightAudit: autoRun,
        autoRunTime: autoRun && autoRunTime.trim() !== '' ? autoRunTime : null,
        warnOnOpenShifts: warnOpenShifts,
        auditRecipients: list,
      },
      { onSuccess: () => onDirtyChange(false) },
    );
  }

  return (
    <FormShell onSubmit={handleSubmit} saveDisabled={!!recipientsError} isPending={upsert.isPending} error={upsert.error} success={upsert.isSuccess}>
      <Checkbox id="cfg-autorun" label="Auto-run night audit" checked={autoRun} onChange={(v) => { setAutoRun(v); touch(); }} />
      <Field id="cfg-autoruntime" label="Auto-run time">
        <input id="cfg-autoruntime" type="time" disabled={!autoRun} value={autoRunTime} onChange={(e) => { setAutoRunTime(e.target.value); touch(); }} className={cn(inputCls, !autoRun && 'bg-gray-100 text-gray-400')} />
      </Field>
      <Checkbox id="cfg-warnshifts" label="Warn on open shifts" checked={warnOpenShifts} onChange={(v) => { setWarnOpenShifts(v); touch(); }} />
      <Field id="cfg-recipients" label="Report recipients (comma-separated)" error={recipientsError}>
        <input id="cfg-recipients" type="text" value={recipients} onChange={(e) => { setRecipients(e.target.value); setRecipientsError(''); touch(); }} onBlur={validateRecipients} placeholder="alice@hotel.com, bob@hotel.com" className={inputCls} />
      </Field>
    </FormShell>
  );
}

// ─── Shift tab ───────────────────────────────────────────────────────────────

function ShiftTab({ propertyId, settings, onDirtyChange }: TabProps) {
  const upsert = useUpsertPosConfig();
  const [threshold, setThreshold] = useState('');
  const [blindDrop, setBlindDrop] = useState(false);
  const [requireShift, setRequireShift] = useState(false);

  useEffect(() => {
    setThreshold(settings?.cashDropAlertThreshold != null ? parseFloat(settings.cashDropAlertThreshold).toFixed(2) : '');
    setBlindDrop(settings?.blindDrop ?? false);
    setRequireShift(settings?.requireShiftOnSale ?? false);
    onDirtyChange(false);
  }, [settings, onDirtyChange]);

  const thresholdNum = parseFloat(threshold);
  const thresholdError = threshold.trim() !== '' && (Number.isNaN(thresholdNum) || thresholdNum < 0)
    ? 'Threshold must be 0 or more.' : '';

  function touch() { onDirtyChange(true); if (upsert.isError) upsert.reset(); }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (thresholdError) return;
    upsert.mutate(
      {
        propertyId,
        cashDropAlertThreshold: threshold.trim() === '' ? null : thresholdNum.toFixed(2),
        blindDrop,
        requireShiftOnSale: requireShift,
      },
      { onSuccess: () => onDirtyChange(false) },
    );
  }

  return (
    <FormShell onSubmit={handleSubmit} saveDisabled={!!thresholdError} isPending={upsert.isPending} error={upsert.error} success={upsert.isSuccess}>
      <Field id="cfg-threshold" label="Cash-drop alert threshold" error={thresholdError}>
        <input id="cfg-threshold" type="number" step="0.01" min="0" value={threshold} onChange={(e) => { setThreshold(e.target.value); touch(); }} placeholder="0.00" className={inputCls} />
      </Field>
      <Checkbox id="cfg-blinddrop" label="Blind drop" checked={blindDrop} onChange={(v) => { setBlindDrop(v); touch(); }} />
      <Checkbox id="cfg-reqshift" label="Require open shift on sale" checked={requireShift} onChange={(v) => { setRequireShift(v); touch(); }} />
    </FormShell>
  );
}
